import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useFormik } from "formik";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateCustomerRelationShipMutation,
  useGetCustomerFilterByPerentQuery,
  useGetCustomerRelationQuery,
  useDeleteRelationShipMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router";
import { RelationshipType } from "../../../Core/Enum/enum";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import {
  ICustomer,
  ICustomerRelative,
} from "../../../Api/Interface/api.interface";
import { FaTrash } from "react-icons/fa";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import NewPopUpProduct from "../Edit/withoutRelative";
import Delete from "@mui/icons-material/Delete";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

interface IRelative {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: ICustomerRelative[];
  customerClientId?: String;
}

const Customer = ({
  openModel = false,
  handleCloseDialog,
  data,
  customerClientId,
}: IRelative) => {
  const classes = useStyles();
  const theme = useTheme();
  const [open] = React.useState(openModel);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [newTax, { isLoading }] = useCreateCustomerRelationShipMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [addedRelative, setAddedRelative] = useState<ICustomerRelative[]>([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [selectedCoursing, setSelectedCoursing] = React.useState<ICustomer>();
  const [openDialogAddProduct, setOpenDialogAddProduct] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const [slotToDelete, setSlotToDelete] = useState<{
    index: number;
    // slotIndex: number | null;
  } | null>(null);

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const {
    data: allClientData,
    isLoading: allClientIsLoading,
    refetch,
  } = useGetCustomerFilterByPerentQuery({
    searchText: currentSearchQuery,
    id: "",
    // isParent: selectedIsBooked,
    isParentFilter: "",
    isParent: "all",
    customerClientId: "all",
  });

  const { data: relationData, isLoading: relationIsLoading } =
    useGetCustomerRelationQuery({
      policyCustomerId: String(customerClientId),
    });

  const coursings: ICustomerRelative[] = Array.isArray(relationData?.data)
    ? (relationData?.data as ICustomerRelative[])
    : [];

  const allCliendUpList = useMemo(() => {
    return (allClientData?.data ?? []) as ICustomer[];
  }, [allClientData?.data]);

  const clientOptions = useMemo(() => {
    if (!Array.isArray(allCliendUpList)) return [];
    return allCliendUpList.map((cust) => ({
      label: `${toProperCase(cust.customerFirstName)} ${toProperCase(
        cust.customerLastName
      )}`,
      value: cust.id,
    }));
  }, [allCliendUpList]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (coursings) {
      setAddedRelative(coursings);
    }
  }, [coursings]);

  const formik = useFormik({
    initialValues: {
      isPerent: Boolean(relationData?.isPerent),
      relationShip: [],
    },
    enableReinitialize: true,

    onSubmit: async (values, { resetForm }) => {
      try {
        if (addedRelative.length > 0) {
          const hasInvalidRelationship = addedRelative.some(
            (rel) => !rel.related_customer_id || !rel.relationship_type
          );

          if (hasInvalidRelationship) {
            showErrorMessage(
              "Please complete all relationship entries (both client and relationship type)."
            );
            return;
          }

          const seen = new Set<number>();
          const hasDuplicate = addedRelative.some((rel) => {
            if (rel.related_customer_id === null) return false;
            if (seen.has(rel.related_customer_id)) return true;
            seen.add(rel.related_customer_id);
            return false;
          });

          if (hasDuplicate) {
            showErrorMessage("Each related client must be unique.");
            return;
          }
        }

        const filteredRelationship = addedRelative.filter(
          (rel) => rel.related_customer_id && rel.relationship_type
        );

        const temData = {
          id: customerClientId,
          relationShip: filteredRelationship,
          isPerent: Boolean(values.isPerent),
        };

        const addCompanyResponse = await newTax(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleFieldChangeRelative = <K extends keyof ICustomerRelative>(
    index: number,
    field: K,
    value: ICustomerRelative[K]
  ) => {
    const updatedRows = addedRelative.map((term, i) => {
      if (i === index) {
        return {
          ...term,
          [field]: value !== undefined ? value : term[field],
        };
      }
      return term;
    });

    setAddedRelative(updatedRows);
  };

  const cancelFunction = () => {
    handleClose();
  };

  const handleDeleteClick = (index: number) => {
    setSlotToDelete({ index });
    setOpenDialog(true);
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
    setSlotToDelete(null);
  };

  const [deleteCategory] = useDeleteRelationShipMutation();

  const handleDeleteConfirm = async () => {
    if (slotToDelete) {
      const { index } = slotToDelete;
      const slotId = addedRelative[index].id;

      try {
        const response = await deleteCategory(String(slotId)).unwrap();
        if (response.status) {
          showMessage(response.message);
          const updated = [...addedRelative];
          updated.splice(index, 1);
          setAddedRelative(updated);
          setOpenDialog(false);
        } else {
          showErrorMessage(response.message);
          setOpenDialog(false);
        }
      } catch (error) {
        setOpenDialog(false);
      }
    }
  };

  if (allClientIsLoading || relationIsLoading)
    return (
      <Dialog
        open={open}
        PaperProps={{
          style: {
            width: "700px",
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10vh"
        >
          <CircularProgress />
        </Box>
      </Dialog>
    );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobile}
      fullWidth={isMobile}
      maxWidth="md"
    >
      <Grid className={classes.Dialog}>
        <Grid container spacing={3}>
        
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          RelationShip
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={5}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container py={1} justifyContent={"space-between"}>
                          <Grid
                            item
                            justifyContent="flex-start"
                            sx={{ display: "flex" }}
                            px={1}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formik.values.isPerent}
                                  onChange={formik.handleChange}
                                  name="isPerent"
                                  color="primary"
                                />
                              }
                              label="Is Parent"
                            />
                          </Grid>
                          <Grid
                            item
                            justifyContent="flex-end"
                            sx={{ display: "flex" }}
                            m={1}
                          >
                            <Button
                              variant="contained"
                              className={classes.addButton}
                              onClick={() => {
                                setSelectedCoursing(undefined);
                                setOpenDialogAddProduct(true);
                              }}
                            >
                              Create Relative
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <TableContainer>
                            <Table className={classes.table}>
                              <TableHead className={classes.tableHeaderRow}>
                                <TableRow>
                                  <TableCell className={classes.tablheader}>
                                    Name
                                  </TableCell>
                                  <TableCell className={classes.tablheader}>
                                    RelationShip
                                  </TableCell>
                                  <TableCell
                                    className={classes.tablheader}
                                    sx={{ textAlign: "end" }}
                                  >
                                    Action
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {addedRelative.map((item, index) => {
                                  const selectedClient =
                                    clientOptions.find(
                                      (opt) =>
                                        opt.value === item.related_customer_id
                                    ) || null;

                                  return (
                                    <TableRow key={index}>
                                      <TableCell className={classes.tablCell}>
                                        <Autocomplete
                                          options={clientOptions}
                                          value={selectedClient}
                                          getOptionLabel={(option) =>
                                            option.label
                                          }
                                          onChange={(_, newValue) => {
                                            handleFieldChangeRelative(
                                              index,
                                              "related_customer_id",
                                              newValue?.value || null
                                            );
                                          }}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              placeholder="Search related clients"
                                              size="small"
                                              className={classes.textField}
                                              InputProps={{
                                                ...params.InputProps,
                                                sx: {
                                                  fontSize: 14,
                                                  border: "none",
                                                  minWidth: "150px",
                                                },
                                                classes: {
                                                  root: classes.textFieldQuote,
                                                  notchedOutline:
                                                    classes.notchedOutline,
                                                },
                                              }}
                                              InputLabelProps={{
                                                sx: {
                                                  fontSize: 14,
                                                  border: "none",
                                                },
                                              }}
                                            />
                                          )}
                                          sx={{ width: "200px" }}
                                          size="small"
                                          className={classes.searchTextField23}
                                        />
                                      </TableCell>

                                      <TableCell className={classes.tablCell}>
                                        <Autocomplete
                                          options={Object.values(
                                            RelationshipType
                                          )}
                                          value={item.relationship_type || ""}
                                          onChange={(event, newValue) =>
                                            handleFieldChangeRelative(
                                              index,
                                              "relationship_type",
                                              newValue || ""
                                            )
                                          }
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              placeholder="Relationship.."
                                              size="small"
                                              className={classes.textField}
                                              InputProps={{
                                                ...params.InputProps,
                                                sx: {
                                                  fontSize: 14,
                                                  border: "none",
                                                  minWidth: "150px",
                                                },
                                                classes: {
                                                  root: classes.textFieldQuote,
                                                  notchedOutline:
                                                    classes.notchedOutline,
                                                },
                                              }}
                                              InputLabelProps={{
                                                sx: {
                                                  fontSize: 14,
                                                  border: "none",
                                                },
                                              }}
                                            />
                                          )}
                                          sx={{ width: "200px" }}
                                          size="small"
                                          className={classes.searchTextField23}
                                        />
                                      </TableCell>

                                      <TableCell className={classes.tablCell}>
                                        <Grid
                                          container
                                          spacing={1}
                                          sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                          }}
                                        >
                                          <Grid item>
                                            <IconButton
                                              color="error"
                                              onClick={() => {
                                                if (item.id === null) {
                                                  const updated = [
                                                    ...addedRelative,
                                                  ];
                                                  updated.splice(index, 1);
                                                  setAddedRelative(updated);
                                                } else {
                                                  handleDeleteClick(index);
                                                }
                                              }}
                                            >
                                              <FaTrash />
                                            </IconButton>
                                          </Grid>
                                        </Grid>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {/* Add Relative Button (Flex End) */}
                          <Grid container justifyContent="flex-end" m={1}>
                            <Button
                              variant="contained"
                              className={classes.addButton}
                              onClick={() =>
                                setAddedRelative((prev) => [
                                  ...prev,
                                  {
                                    related_customer_id: null,
                                    relationship_type: "",
                                    id: null,
                                    customer_id: null,
                                  },
                                ])
                              }
                            >
                              Add Relative
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid container spacing={3} p={"24px"}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={cancelFunction}
                        >
                          Close
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<CheckCircleOutlineIcon />}
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                        >
                          Update
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </form>
          </Grid>
          {openDialogAddProduct && (
            <NewPopUpProduct
              openModel={openDialogAddProduct}
              handleCloseDialog={() => setOpenDialogAddProduct(false)}
            />
          )}
        </Grid>

        <Dialog
          open={openDialog}
          onClose={handleDeleteCancel}
          sx={{ padding: 5, minWidth: 450 }}
        >
          <Grid >
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>
                  Delete Relationship
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <DialogContent>
            <DialogContentText sx={{ pt: 3 }}>
              Are you sure you want to delete this relationship?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ paddingRight: "24px", p: 4, py: 3, gap: 2 }}>
            <Grid container spacing={3}>
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  className={classes.addButton}
                  onClick={handleDeleteCancel}
                >
                  Cancel
                </Button>
                <Box m={0.5}></Box>

                <Button
                  variant="contained"
                  startIcon={<Delete />}
                  className={classes.addButton}
                  onClick={handleDeleteConfirm}
                >
                  Confirm
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </Dialog>
      </Grid>
    </Dialog>
  );
};

export default Customer;
